"use client";

import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { spoonflowerDesigns, spoonflowerCategories } from "@/lib/data";

function FabricCard({ design, index }: { design: (typeof spoonflowerDesigns)[0]; index: number }) {
  const [imgError, setImgError] = useState(false);

  return (
    <motion.a
      href={design.link}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: Math.min(index * 0.015, 0.4), duration: 0.3 }}
      className="group relative overflow-hidden rounded-xl aspect-square block bg-[#1a1a1a]"
    >
      {/* Real image */}
      {!imgError ? (
        <img
          src={design.imageUrl}
          alt={design.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          onError={() => setImgError(true)}
          loading="lazy"
        />
      ) : (
        /* Fallback gradient */
        <div className="w-full h-full bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] flex items-center justify-center">
          <span className="text-white/20 text-4xl">✦</span>
        </div>
      )}

      {/* Bottom title overlay - always visible */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-3 pt-8">
        <p className="text-white text-xs font-semibold leading-tight line-clamp-2">
          {design.title}
        </p>
        <span className="text-[#c9a84c] text-[9px] font-medium uppercase tracking-wide mt-0.5 block">
          {design.category}
        </span>
      </div>

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
        <div className="text-center px-3">
          <ExternalLink className="w-6 h-6 text-white mx-auto mb-2" />
          <p className="text-white text-sm font-semibold">Shop on Spoonflower</p>
          <p className="text-white/60 text-[10px] mt-1">{design.category}</p>
        </div>
      </div>
    </motion.a>
  );
}

export function FabricGallery() {
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filtered = useMemo(() => {
    if (selectedCategory === "All") return spoonflowerDesigns;
    return spoonflowerDesigns.filter((d) => d.category === selectedCategory);
  }, [selectedCategory]);

  return (
    <section id="fabric-designs" className="py-24 px-4 md:px-8 bg-[#0a0a0a]">
      <div className="max-w-8xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#c9a84c] text-sm font-semibold tracking-widest uppercase mb-3">
            Fabric & Textile Designs
          </p>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-4">
            Spoonflower Collection
          </h2>
          <div className="section-divider mb-6" />
          <p className="text-gray-400 max-w-2xl mx-auto">
            873 fabric designs and 868 wallpaper patterns — available for purchase
            as custom fabric, wallpaper, and home decor on Spoonflower. Each design
            links directly to the shop.
          </p>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12 max-w-2xl mx-auto">
          {[
            { label: "Fabric Designs", value: "873" },
            { label: "Wallpaper Designs", value: "868" },
            { label: "Tags / Themes", value: "300+" },
            { label: "Since", value: "2015" },
          ].map((s) => (
            <div key={s.label} className="text-center bg-[#1a1a1a] rounded-xl p-4 border border-[#222]">
              <div className="font-serif text-2xl font-bold text-[#c9a84c]">{s.value}</div>
              <div className="text-gray-500 text-xs mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {spoonflowerCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                selectedCategory === cat
                  ? "bg-[#c9a84c] text-black"
                  : "bg-[#1e1e1e] text-gray-400 hover:bg-[#2a2a2a] hover:text-white border border-[#333]"
              }`}
            >
              {cat}
              {cat !== "All" && (
                <span className="ml-1.5 text-xs opacity-60">
                  ({spoonflowerDesigns.filter((d) => d.category === cat).length})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedCategory}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="spoonflower-grid"
          >
            {filtered.map((design, idx) => (
              <FabricCard key={design.id} design={design} index={idx} />
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Results count */}
        <p className="text-center text-gray-600 text-sm mt-6">
          Showing {filtered.length} of {spoonflowerDesigns.length} featured designs
        </p>

        {/* View more */}
        <div className="text-center mt-8">
          <a
            href="https://www.spoonflower.com/profiles/overt_ink"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-[#c9a84c] text-[#c9a84c] px-8 py-3 rounded-full hover:bg-[#c9a84c] hover:text-black transition-all duration-200 font-medium"
          >
            Browse All 873+ Fabric Designs on Spoonflower →
          </a>
        </div>
      </div>
    </section>
  );
}
